import React from "react";
import { Typography, Card, Space, Form, Input, Button, Row, Col } from "antd";
import {
  MailOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

const Contact: React.FC = () => {
  const [form] = Form.useForm();

  const handleSubmit = (values: any) => {
    console.log("Contact form submitted:", values);
    // Here you would typically send the form data to your backend
    form.resetFields();
  };

  return (
    <div
      style={{
        maxWidth: "1000px",
        margin: "0 auto",
        padding: "2rem",
        minHeight: "100vh",
        backgroundColor: "#f5f5f5",
      }}
    >
      <Row gutter={[32, 32]}>
        <Col xs={24} lg={12}>
          <Card>
            <Title level={1}>Contact Us</Title>
            <Paragraph>
              We'd love to hear from you! Whether you have questions about our
              agricultural intelligence platform, need technical support, or
              want to discuss partnership opportunities, our team is here to
              help.
            </Paragraph>

            <div style={{ marginTop: "2rem" }}>
              <Title level={3}>Get in Touch</Title>

              <Space
                direction="vertical"
                size="large"
                style={{ width: "100%" }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  <MailOutlined
                    style={{ fontSize: "18px", color: "#1890ff" }}
                  />
                  <div>
                    <Text strong>Email</Text>
                    <br />
                    <Text>support@expedient.one</Text>
                  </div>
                </div>

                <div
                  style={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  <PhoneOutlined
                    style={{ fontSize: "18px", color: "#1890ff" }}
                  />
                  <div>
                    <Text strong>Phone</Text>
                    <br />
                    <Text>+1 (555) 123-4567</Text>
                  </div>
                </div>

                <div
                  style={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  <EnvironmentOutlined
                    style={{ fontSize: "18px", color: "#1890ff" }}
                  />
                  <div>
                    <Text strong>Address</Text>
                    <br />
                    <Text>
                      Expedient AI, Inc.
                      <br />
                      123 Innovation Drive
                      <br />
                      Boston, MA 02101
                      <br />
                      United States
                    </Text>
                  </div>
                </div>
              </Space>
            </div>

            <div style={{ marginTop: "2rem" }}>
              <Title level={3}>Business Hours</Title>
              <Paragraph>
                <Text strong>Monday - Friday:</Text> 9:00 AM - 6:00 PM EST
                <br />
                <Text strong>Saturday:</Text> 10:00 AM - 4:00 PM EST
                <br />
                <Text strong>Sunday:</Text> Closed
              </Paragraph>
              <Text type="secondary">
                For urgent technical issues, please email us and we'll respond
                as soon as possible.
              </Text>
            </div>

            <div style={{ marginTop: "2rem" }}>
              <Title level={3}>Other Ways to Connect</Title>
              <Space direction="vertical" size="small">
                <Text>
                  📧 <strong>General Inquiries:</strong> info@expedient.one
                </Text>
                <Text>
                  🔧 <strong>Technical Support:</strong> support@expedient.one
                </Text>
                <Text>
                  🤝 <strong>Partnerships:</strong> partnerships@expedient.one
                </Text>
                <Text>
                  📰 <strong>Media & Press:</strong> press@expedient.one
                </Text>
                <Text>
                  🔒 <strong>Privacy & Security:</strong> privacy@expedient.one
                </Text>
              </Space>
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card>
            <Title level={2}>Send us a Message</Title>
            <Paragraph>
              Have a specific question or need personalized assistance? Fill out
              the form below and we'll get back to you within 24 hours.
            </Paragraph>

            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              style={{ marginTop: "1rem" }}
            >
              <Form.Item
                name="name"
                label="Full Name"
                rules={[{ required: true, message: "Please enter your name" }]}
              >
                <Input placeholder="Your full name" />
              </Form.Item>

              <Form.Item
                name="email"
                label="Email Address"
                rules={[
                  { required: true, message: "Please enter your email" },
                  { type: "email", message: "Please enter a valid email" },
                ]}
              >
                <Input placeholder="your.email@example.com" />
              </Form.Item>

              <Form.Item name="company" label="Company/Organization">
                <Input placeholder="Your company name (optional)" />
              </Form.Item>

              <Form.Item
                name="subject"
                label="Subject"
                rules={[{ required: true, message: "Please enter a subject" }]}
              >
                <Input placeholder="What is this regarding?" />
              </Form.Item>

              <Form.Item
                name="message"
                label="Message"
                rules={[
                  { required: true, message: "Please enter your message" },
                ]}
              >
                <TextArea
                  rows={6}
                  placeholder="Tell us how we can help you..."
                  maxLength={1000}
                  showCount
                />
              </Form.Item>

              <Form.Item>
                <Button type="primary" htmlType="submit" size="large" block>
                  Send Message
                </Button>
              </Form.Item>
            </Form>

            <div
              style={{
                marginTop: "2rem",
                padding: "1rem",
                backgroundColor: "#f0f2f5",
                borderRadius: "8px",
              }}
            >
              <Text type="secondary" style={{ fontSize: "12px" }}>
                By submitting this form, you agree to our{" "}
                <Link to="/privacy">Privacy Policy</Link> and{" "}
                <Link to="/terms">Terms of Service</Link>. We'll only use your
                information to respond to your inquiry.
              </Text>
            </div>
          </Card>
        </Col>
      </Row>

      <div
        style={{
          marginTop: "3rem",
          textAlign: "center",
          padding: "2rem",
          backgroundColor: "#fff",
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <Title level={3}>Frequently Asked Questions</Title>
        <Paragraph>
          Before reaching out, you might find the answer to your question in our
          FAQ section.
        </Paragraph>
        <Space>
          <Button type="default">
            <Link to="/faq">View FAQ</Link>
          </Button>
          <Button type="default">
            <Link to="/docs">Documentation</Link>
          </Button>
          <Button type="primary">
            <Link to="/">Back to Home</Link>
          </Button>
        </Space>
      </div>
    </div>
  );
};

export default Contact;
